import { FC, ReactNode } from 'react';
import { Button, InlineLoading, Modal, ProgressBar, Tag } from '@carbon/react';
import { format0 } from 'utils/number';
import request from 'lib/request';
import type { DataTableColumnDef } from 'components/data-table';
import type {
  BatchStatus,
  BulkType,
  MatchType,
  ProcessStatus,
  Run,
  StartProcessResponse,
  SubclusterStatus,
} from './types';
import {
  STATUS_META,
  SUBCLUSTER_STATUSES,
  subclusterStatusLabel,
  type TagColor,
} from './status-vocabulary';
import styles from './page.module.scss';

export const JOB_TYPE_LABELS: Record<string, string> = {
  imported_imported: 'Imported → Imported',
  all: 'All',
  imported_typed: 'Imported → Typed (legacy)',
  identical: 'Identical text only',
  // An address run's union rule (identical text or standardized form).
  standardized: 'All',
};

export const BULK_TYPE_LABELS: Record<BulkType, string> = {
  entities: 'Entities',
  addresses: 'Addresses',
};

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  generating: 'Generating',
  generation_failed: 'Generation failed',
  ready: 'Ready',
  executing: 'Executing',
  executed: 'Executed',
  executed_with_failures: 'Executed with failures',
  reversed: 'Reversed',
};

// Runs still doing work — the hub polls while any is.
export const ACTIVE_BATCH_STATUSES: ReadonlySet<string> = new Set([
  'generating',
  'executing',
]);

// One POST → StartProcessResponse unwrap; the success/error/else branch was
// copy-pasted per call site and the fallback strings had started to drift.
export async function startProcessRequest(
  url: string,
  body: Record<string, unknown>
): Promise<{ data?: StartProcessResponse; error?: string }> {
  const res = await request.post<StartProcessResponse, { error: string }>(
    url,
    body,
    new AbortController()
  );
  if (res.type === 'success') return { data: res.data };
  if (res.type === 'error')
    return { error: res.data?.error ?? 'Request failed' };
  return { error: res.message };
}

// The "#id · timestamp" first column shared by the hub and the Executions
// panel.
export function runColumn<
  TRow extends Record<string, unknown> & { id: number; createdAt: string },
>(header: string): DataTableColumnDef<TRow> {
  return {
    id: 'run',
    header,
    size: 220,
    enableSorting: false,
    cell: ({ row }) => {
      const r = row.original;
      return (
        <span>
          <strong>#{r.id}</strong>{' '}
          <span className={styles.addressLine}>
            {new Date(r.createdAt).toLocaleString()}
          </span>
        </span>
      );
    },
  };
}

// The Executions panel shared by entity and address runs. Reverse is blocked
// while a merge or reversal runs: either would race the reversal's writes.
// Only an address group can be partially reversed, and only an address run
// moves document references, so those two counts show on address runs only.
// `appliedHeader` stays explicit: it doesn't follow the bulk type ("Merged" vs
// "Deduplicated"/"Combined").
export function executionColumns({
  appliedHeader,
  bulkType,
  reverseBlocked,
  onReverse,
}: {
  appliedHeader: string;
  bulkType: BulkType;
  reverseBlocked: boolean;
  onReverse: (run: Run) => void;
}): DataTableColumnDef<Run>[] {
  const addressRun = bulkType === 'addresses';
  const count = (
    accessorKey:
      | 'combined'
      | 'failed'
      | 'reversed'
      | 'partiallyReversed'
      | 'documentReferencesMoved',
    header: string
  ): DataTableColumnDef<Run> => ({
    accessorKey,
    header,
    size: 120,
    enableSorting: false,
    cell: ({ getValue }) => format0(getValue() as number),
  });
  return [
    runColumn<Run>('Execution'),
    {
      accessorKey: 'actor',
      header: 'Executed by',
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => (getValue() as string | null) ?? '--',
    },
    count('combined', appliedHeader),
    ...(addressRun
      ? [count('documentReferencesMoved', 'Citations moved')]
      : []),
    count('failed', 'Failed'),
    count('reversed', 'Reversed'),
    ...(addressRun ? [count('partiallyReversed', 'Partially reversed')] : []),
    {
      id: 'reverse',
      header: '',
      size: 200,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          size="sm"
          kind="danger--ghost"
          disabled={reversibleInRun(row.original) === 0 || reverseBlocked}
          onClick={() => onReverse(row.original)}
        >
          Reverse
        </Button>
      ),
    },
  ];
}

// Groups of an execution a reversal can still (further) undo.
const reversibleInRun = (run: Run) =>
  run.combined + (run.partiallyReversed ?? 0);

// Danger confirmation for a reversal; children describe what it restores.
export const ReverseConfirmModal: FC<{
  open: boolean;
  heading: string;
  confirmText: string;
  // What is being undone, for the closing warning ("this execution").
  subject: string;
  onClose: () => void;
  onConfirm: () => void;
  children: ReactNode;
}> = ({
  open,
  heading,
  confirmText,
  subject,
  onClose,
  onConfirm,
  children,
}) => (
  <Modal
    danger
    open={open}
    modalHeading={heading}
    primaryButtonText={confirmText}
    secondaryButtonText="Cancel"
    onRequestClose={onClose}
    onRequestSubmit={onConfirm}
  >
    <p>{children}</p>
    <p>
      <strong>This affects live entity data.</strong> Only reverse if you are
      sure this {subject} should be undone.
    </p>
  </Modal>
);

export const ProcessProgress: FC<{
  label: string;
  status: ProcessStatus | null;
}> = ({ label, status }) => {
  if (!status) return <InlineLoading description={`${label}: starting…`} />;
  const {
    total_steps: total,
    completed_steps: done,
    failed_steps: failed,
  } = status;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={styles.progress}>
      <ProgressBar
        label={label}
        helperText={`${format0(done)} / ${format0(total)} steps${
          failed ? ` · ${format0(failed)} failed` : ''
        } — ${status.overall_status}`}
        value={pct}
        max={100}
        status={
          status.overall_status === 'processed'
            ? 'finished'
            : status.overall_status === 'failed'
              ? 'error'
              : 'active'
        }
      />
    </div>
  );
};

// Renders the matched concordant address(es) — one per line so transitive
// clusters show every address that drove the match.
export const AddressList: FC<{ values: string[] }> = ({ values }) => {
  if (!values || values.length === 0) return <>--</>;
  return (
    <div role="list">
      {values.map((addr, i) => (
        // A member can carry the same normalized address twice, so the value
        // alone isn't a stable key; the list is static and never reordered.
        <div
          key={`${i}-${addr}`}
          role="listitem"
          className={styles.addressItem}
        >
          {addr}
        </div>
      ))}
    </div>
  );
};

// How a sub-cluster's members were linked. "Both" = the cluster contains a mix
// of address-linked and alias-linked edges.
const MATCH_TYPE_LABELS: Record<string, { label: string; color: TagColor }> = {
  address: { label: 'Address', color: 'blue' },
  alias: { label: 'Alias-confirmed', color: 'teal' },
  both: { label: 'Address + alias', color: 'purple' },
};

export const MatchTypeTag: FC<{ value: MatchType | null }> = ({ value }) => {
  const meta = value ? MATCH_TYPE_LABELS[value] : undefined;
  if (!meta) return <>--</>;
  return (
    <Tag type={meta.color} size="sm">
      {meta.label}
    </Tag>
  );
};

// Colours for the workflow-process and batch lifecycles; the sub-cluster
// statuses come from the one status vocabulary (its entity colours, which the
// shared entity/batch tag uses — address tags carry their own).
const STATUS_TAG_COLORS: Record<string, TagColor> = {
  processed: 'green',
  processing: 'blue',
  pending: 'cool-gray',
  generating: 'blue',
  generation_failed: 'red',
  ready: 'teal',
  executing: 'blue',
  executed: 'green',
  executed_with_failures: 'magenta',
  ...Object.fromEntries(
    SUBCLUSTER_STATUSES.map((s) => [s, STATUS_META[s].color.entities])
  ),
};

export function statusTagColor(status: string): TagColor {
  return STATUS_TAG_COLORS[status] ?? 'cool-gray';
}

export const StatusTag: FC<{ status: string; bulkType: BulkType }> = ({
  status,
  bulkType,
}) => {
  // A sub-cluster status reads its label (which varies per run type) off the one
  // status vocabulary; anything else is a batch status, falling back to the raw
  // value for an unknown one.
  const label = SUBCLUSTER_STATUSES.includes(status as SubclusterStatus)
    ? subclusterStatusLabel(status as SubclusterStatus, bulkType)
    : BATCH_STATUS_LABELS[status as BatchStatus] ?? status;
  return (
    <Tag type={statusTagColor(status)} size="sm">
      {label}
    </Tag>
  );
};

// DataTable config with no toolbar/sort/customization — used for the small
// static tables. columnResizing must be off too: any customization feature
// enables the toolbar's reset button, which belongs on the main table only.
export const PLAIN_TABLE_CONFIG = {
  sorting: false,
  globalSearch: false,
  columnFilters: false,
  columnVisibility: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  export: false,
} as const;
